import { QuantityBreaksUpdatedEvent } from '@theme/events';

class BundlePack extends HTMLElement {
  constructor() {
    super();
    this.cards = [];
    this.meterGroups = [];
    this.formatToggle = null;
    this.activeFormat = '';
  }

  connectedCallback() {
    this.cards = Array.from(this.querySelectorAll('[data-bundle-pack-card]'));
    this.formatToggle = this.querySelector('[data-bundle-pack-format]');
    this.meterGroups = Array.from(this.querySelectorAll('[data-bundle-pack-meter]')).map((meterEl) =>
      this.#buildMeterGroup(meterEl),
    );

    this.messageEl = this.querySelector('[data-bundle-pack-message]');
    this.messageText = this.messageEl?.querySelector('[data-bundle-pack-message-text]');
    try {
      this.messageTiers = JSON.parse(this.messageEl?.dataset.tiers || '[]');
    } catch (e) {
      this.messageTiers = [];
    }

    this.cards.forEach((card) => this.#bindCard(card));

    if (this.formatToggle) {
      this.#bindFormatToggle();
      // Chargement initial : on respecte les quantités par défaut du format actif sans tout réinitialiser.
      this.#setFormat(this.formatToggle.dataset.defaultFormat || '250g', { resetQuantities: false });
    } else {
      this.#update();
      this.#alignLineToCircles();
    }

    if (typeof ResizeObserver !== 'undefined') {
      this._ro = new ResizeObserver(() => this.#alignLineToCircles());
      this._ro.observe(this);
    }
  }

  #buildMeterGroup(meterEl) {
    const line = meterEl.querySelector('.bundle-pack-meter__line');
    const fill = meterEl.querySelector('[data-bundle-pack-meter-fill]');
    const tiers = Array.from(meterEl.querySelectorAll('[data-bundle-pack-meter-tier]')).map((el) => ({
      el,
      qty: parseInt(el.dataset.tierQty, 10) || 0,
      circle: el.querySelector('.bundle-pack-meter__tier-circle'),
    }));

    let tierConfig = [];
    try {
      tierConfig = JSON.parse(meterEl.dataset.tiers || '[]');
    } catch (e) {
      tierConfig = [];
    }

    return {
      el: meterEl,
      format: meterEl.dataset.format || '',
      line,
      fill,
      tiers,
      tierConfig,
    };
  }

  #bindFormatToggle() {
    this.formatToggle.querySelectorAll('[data-bundle-pack-format-button]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.#setFormat(btn.dataset.format);
      });
    });
  }

  #setFormat(format, { resetQuantities = true } = {}) {
    if (format === this.activeFormat) return;

    this.activeFormat = format;

    if (resetQuantities) {
      this.cards.forEach((card) => {
        const input = card.querySelector('.bundle-pack-card__quantity-input');
        if (input) this.#setCardQty(card, input, 0, { skipUpdate: true });
      });
    }

    this.#applyFormatVisibility();
    this.#update();
    this.#alignLineToCircles();
  }

  #applyFormatVisibility() {
    this.cards.forEach((card) => {
      card.hidden = !this.#isFormatMatch(card.dataset.format);
    });

    this.meterGroups.forEach((group) => {
      group.el.hidden = !this.#isFormatMatch(group.format);
    });

    if (this.formatToggle) {
      this.formatToggle.querySelectorAll('[data-bundle-pack-format-button]').forEach((btn) => {
        const isActive = btn.dataset.format === this.activeFormat;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-pressed', String(isActive));
      });
    }
  }

  // Un format vide sur une carte/jauge = élément "universel" (rétrocompatibilité avec les bundle packs sans choix de format).
  #isFormatMatch(format) {
    if (!format) return true;
    if (!this.activeFormat) return true;
    return format === this.activeFormat;
  }

  #getActiveCards() {
    return this.cards.filter((card) => this.#isFormatMatch(card.dataset.format));
  }

  #getActiveMeterGroup() {
    return this.meterGroups.find((group) => this.#isFormatMatch(group.format)) || this.meterGroups[0] || null;
  }

  #alignLineToCircles() {
    const group = this.#getActiveMeterGroup();
    if (!group || !group.line || group.tiers.length < 2) return;
    const sorted = [...group.tiers].sort((a, b) => a.qty - b.qty);
    const first = sorted[0].circle;
    const last = sorted[sorted.length - 1].circle;
    if (!first || !last) return;
    const trackRect = group.line.parentElement.getBoundingClientRect();
    if (trackRect.width === 0) return;
    const firstRect = first.getBoundingClientRect();
    const lastRect = last.getBoundingClientRect();
    const leftPx = firstRect.left - trackRect.left + firstRect.width / 2;
    const rightPx = trackRect.right - lastRect.right + lastRect.width / 2;
    group.line.style.left = leftPx + 'px';
    group.line.style.right = rightPx + 'px';
  }

  #bindCard(card) {
    const input = card.querySelector('.bundle-pack-card__quantity-input');
    if (!input) return;

    card.querySelectorAll('[data-bundle-pack-action]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const current = parseInt(input.value, 10) || 0;
        const delta = btn.dataset.bundlePackAction === 'increment' ? 1 : -1;
        this.#setCardQty(card, input, current + delta);
      });
    });
  }

  #setCardQty(card, input, next, { skipUpdate = false } = {}) {
    const max = parseInt(input.dataset.max, 10) || 999;
    const v = Math.max(0, Math.min(max, next));
    input.value = v;
    card.classList.toggle('bundle-pack-card--selected', v > 0);
    if (!skipUpdate) this.#update();
  }

  #update() {
    const total = this.#getTotalQuantity();
    const group = this.#getActiveMeterGroup();

    if (group) {
      const sortedTiers = [...group.tiers].sort((a, b) => a.qty - b.qty);

      if (group.fill && sortedTiers.length > 1) {
        const lastIdx = sortedTiers.length - 1;
        let fillPct = 0;
        if (total >= sortedTiers[lastIdx].qty) {
          fillPct = 100;
        } else {
          for (let i = 0; i < lastIdx; i++) {
            const curr = sortedTiers[i];
            const next = sortedTiers[i + 1];
            if (total >= curr.qty && total < next.qty) {
              const segmentFrac = (total - curr.qty) / (next.qty - curr.qty);
              fillPct = ((i + segmentFrac) / lastIdx) * 100;
              break;
            }
          }
        }
        group.fill.style.width = fillPct + '%';
      }

      let activeIdx = -1;
      sortedTiers.forEach((t, i) => {
        if (total >= t.qty) activeIdx = i;
      });
      group.tiers.forEach((t) => {
        t.el.dataset.reached = total >= t.qty ? 'true' : 'false';
        t.el.dataset.active = activeIdx >= 0 && t === sortedTiers[activeIdx] ? 'true' : 'false';
      });
    }

    this.#updateMessage(total);

    this.dispatchEvent(new QuantityBreaksUpdatedEvent());
  }

  #updateMessage(total) {
    if (!this.messageEl || !this.messageText) return;

    const sorted = [...this.messageTiers].sort((a, b) => a.qty - b.qty);
    const firstQty = sorted[0]?.qty ?? 1;

    let template;
    let activeTier = null;
    let nextTier = null;

    if (total < firstQty) {
      template = this.messageEl.dataset.emptyMessage || '';
    } else {
      for (let i = 0; i < sorted.length; i++) {
        if (total >= sorted[i].qty) activeTier = sorted[i];
      }
      nextTier = sorted.find((t) => t.qty > total) || null;
      template = activeTier?.message || '';
    }

    const percent = this.#parseDiscountPercentFromBadge(activeTier?.badge);
    const remaining = nextTier ? Math.max(0, nextTier.qty - total) : 0;

    this.messageText.textContent = template
      .replace(/\{qty\}/g, String(total))
      .replace(/\{percent\}/g, String(percent))
      .replace(/\{remaining\}/g, String(remaining));
  }

  #getTotalQuantity() {
    return this.#getActiveCards().reduce((total, card) => {
      const input = card.querySelector('.bundle-pack-card__quantity-input');
      return total + (parseInt(input?.value, 10) || 0);
    }, 0);
  }

  #getActiveTier() {
    const total = this.#getTotalQuantity();
    const group = this.#getActiveMeterGroup();
    const config = group ? group.tierConfig : [];
    const sorted = [...config].sort((a, b) => b.qty - a.qty);
    return sorted.find((t) => total >= t.qty) || null;
  }

  #parseDiscountPercentFromBadge(badge) {
    if (!badge) return 0;
    const match = String(badge).match(/(\d+)\s*%/);
    return match ? parseInt(match[1], 10) : 0;
  }

  #getTierDiscountPercent(tier) {
    if (!tier) return 0;
    const explicit = Number(tier.discount);
    if (Number.isFinite(explicit) && explicit > 0) return explicit;
    return this.#parseDiscountPercentFromBadge(tier.badge);
  }

  getSelectedQuantity() {
    return { dataset: { quantity: String(this.#getTotalQuantity()) } };
  }

  getPrices() {
    let compareAtPrice = 0;
    let basePrice = 0;
    const lines = [];

    this.#getActiveCards().forEach((card) => {
      const input = card.querySelector('.bundle-pack-card__quantity-input');
      const qty = parseInt(input?.value, 10) || 0;
      if (qty <= 0) return;

      const unitPrice = parseInt(card.dataset.variantPrice, 10) || 0;
      const unitCompare = parseInt(card.dataset.variantCompareAtPrice, 10) || unitPrice;

      basePrice += unitPrice * qty;
      compareAtPrice += unitCompare * qty;
      lines.push({ qty, unitPrice });
    });

    const tier = this.#getActiveTier();
    const discountPct = this.#getTierDiscountPercent(tier);

    let price = basePrice;
    if (discountPct > 0) {
      price = lines.reduce((total, line) => {
        const unitDiscounted = Math.round((line.unitPrice * (100 - discountPct)) / 100);
        return total + unitDiscounted * line.qty;
      }, 0);
    }

    return { price, compareAtPrice: Math.max(compareAtPrice, price) };
  }

  getBundlePriceBeforeDiscount() {
    let price = 0;
    this.#getActiveCards().forEach((card) => {
      const input = card.querySelector('.bundle-pack-card__quantity-input');
      const qty = parseInt(input?.value, 10) || 0;
      if (qty <= 0) return;
      price += (parseInt(card.dataset.variantPrice, 10) || 0) * qty;
    });
    return price;
  }

  getDiscount() {
    const tier = this.#getActiveTier();
    const percentage = this.#getTierDiscountPercent(tier);
    return { type: 'percentage', value: percentage, percentage };
  }

  getCrossSellPrices() {
    return { price: 0, compareAtPrice: 0 };
  }

  getSelectedItems() {
    const items = [];
    this.#getActiveCards().forEach((card) => {
      const input = card.querySelector('.bundle-pack-card__quantity-input');
      const qty = parseInt(input?.value, 10) || 0;
      const variantId = card.dataset.variantId;
      if (qty > 0 && variantId) {
        items.push({
          id: variantId,
          quantity: qty,
          unitPrice: parseInt(card.dataset.variantPrice, 10) || 0,
        });
      }
    });
    return items;
  }

  isVariantSelectorActivated() {
    return true;
  }
}

if (!customElements.get('bundle-pack')) {
  customElements.define('bundle-pack', BundlePack);
}
